import { useEffect, useRef, useState } from "react";

// material ui
import { Theme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

// material-ui icons
import PeopleIcon from "@mui/icons-material/PeopleRounded";

// Project import
import Button from "components/@extended/LoadingButton";
import { useTranslation } from "utils/locales/utilityFunctions";
import ShareDialog, { ContactType } from "./shareDialog";
import getEstateMaps from "./getEstateMaps";
import { canManageAudiencePublication } from "utils/audiencePublication";
import useEstatesAndMaps from "hooks/useEstatesAndMaps";
import useAuth from "hooks/useAuth";
import { EstateProps, MapProps, ProjectProps } from "types/estatesAndMap";
import getDataOptions from "components/estatesAndMapSelecter/getDataOptions";
import { DataShareItem, RoleTypes, TeamMember } from "types/auth";
import getContactOptions from "components/estatesAndMapSelecter/getContactOptions";
import IconButton from "components/@extended/IconButton";
import useAppFunctionalityConfig from "hooks/useAppFunctionalityConfig";
import { mixpanelEvents } from "config/servicesConfig/mixpanelEvents";
import { getEmailLanguageFromUserOrg } from "utils/databaseUtils";

export interface ShareDataProps {
  withDataSelector?: boolean;
  withPersonSelector?: boolean;
  withButton?: boolean;
  withAudience?: boolean;
  openModal?: boolean;
  selectedPerson?: ContactType;
  resetExtOpenModal?: (state: boolean) => void;
}
const ShareData = ({
  withButton,
  withDataSelector,
  withPersonSelector,
  withAudience: requestedWithAudience,
  openModal,
  selectedPerson,
  resetExtOpenModal,
}: ShareDataProps) => {
  const matchesSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  // --- Hooks --- //
  const { config } = useAppFunctionalityConfig();
  const withAudience = requestedWithAudience && config.teamAndContactsPage.shareDataPublicly === "enabled";
  const { translate } = useTranslation();
  const {
    selectedEstateId,
    selectedMapId,
    selectedProjectId,
    estates,
    orgEstates,
    maps: userMaps,
    teamMaps,
    teamProjects,
    orgMaps,
    projects,
    orgProjects,
    setSelectedEstateId,
    setSelectedMapId,
    setSelectedProjectId,
  } = useEstatesAndMaps();
  const { user, userOrg, orgTeam, orgContacts, cloudFunctionError, callCloudFunction } = useAuth();
  // --- State --- //
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<null | "map" | "estate" | "project">(null);
  const [teamList, setTeamList] = useState<ContactType[] | null>(null);
  const [contactList, setContactList] = useState<ContactType[] | null>(null);

  // --- References --- //
  const [dataOptions, setDataOptions] = useState<{
    list: any[];
    uniqueEstates: string[];
    estateIdsAndNames?: { [key: string]: string };
  }>({ list: [], uniqueEstates: [], estateIdsAndNames: {} });
  const [selectedData, setSelectedData] = useState<null | MapProps | EstateProps | ProjectProps>(
    null,
  );
  const estateMaps = useRef<null | MapProps[]>(null);
  const categories = useRef<string[] | undefined>(undefined);

  // --- Data --- //
  // Roles
  const ROLE_OPTIONS = [
    { id: "viewer" as RoleTypes, status: "enabled", label: translate("role-view") },
    { id: "commentator" as RoleTypes, status: "enabled", label: translate("role-comment") },
    { id: "editor" as RoleTypes, status: "enabled", label: translate("role-edit") },
    { id: "admin" as RoleTypes, status: "enabled", label: translate("role-admin") },
  ];

  // --- Effects --- //
  useEffect(() => {
    if (orgTeam && user) {
      const newList = Object.entries(orgTeam)
        .map(([id, el]: [string, TeamMember]) => {
          const name = el.name && el.name !== "" ? el.name : el.email;
          return {
            ...el,
            id: id,
            name: name,
            label: name + ":" + el.email,
            personType: "team" as any,
            personTypeLabel: translate("team"),
            personTypeLabelText: translate("team"),
          };
        })
        .filter((el) => el.id !== user.id);
      setTeamList(newList);
    }
  }, [orgTeam, translate("render-change")]);
  useEffect(() => {
    if (orgContacts && selectedData) {
      const newList = getContactOptions({
        userId: user ? user!.id! : null,
        contacts: orgContacts,
        selectedData: selectedData,
        orgMapIds: Object.values(orgMaps).map((el) => el.id),
        orgProjectIds: Object.values(orgProjects).map((el) => el.id),
        personTypeLabelText: translate("contacts"),
      });
      setContactList(newList);
    }
  }, [orgContacts, selectedData, translate("render-change")]);

  useEffect(() => {
    if (loading && cloudFunctionError) {
      setLoading(false);
    }
  }, [cloudFunctionError]);

  useEffect(() => {
    const nextDataOptions = getDataOptions({
      user,
      estates,
      orgEstates,
      maps: userMaps,
      teamMaps,
      orgMaps,
      projects,
      teamProjects,
      orgProjects,
      selectedPerson,
      withArchivedData: true,
      overviewText: translate("overview"),
      allRoleOptions: ROLE_OPTIONS,
      externalText: translate("external").toLowerCase(),
      withoutBuyerRole: true,
    });
    if (selectedPerson?.personType === "audience") {
      const allMaps = { ...Object.assign({}, ...Object.values(teamMaps)), ...orgMaps, ...userMaps };
      nextDataOptions.list = nextDataOptions.list.filter(
        (option) => option.dataElType === "map"
          ? canManageAudiencePublication(user, userOrg, allMaps[option.mapId], { ...orgEstates, ...estates })
          : (option.dataElType === "estate" || option.dataElType === "extEstate")
            && canManageAudiencePublication(user, userOrg, estates[option.estateId] || orgEstates[option.estateId]),
      );
    }
    setDataOptions(nextDataOptions);
  }, [
    user?.orgId, user?.orgRole, user?.authType, userOrg?.id,
    JSON.stringify(teamMaps),
    JSON.stringify(estates),
    JSON.stringify(orgEstates),
    JSON.stringify(userMaps),
    JSON.stringify(orgMaps),
    JSON.stringify(projects),
    JSON.stringify(teamProjects),
    JSON.stringify(orgProjects),
    JSON.stringify(selectedPerson),
    translate("render-change"),
  ]);

  useEffect(() => {
    if (openModal && !modalOpen) {
      setModalOpen(true);
    }
  }, [openModal]);

  useEffect(() => {
    if (selectedProjectId) {
      if (projects[selectedProjectId]) {
        setSelectedData(projects[selectedProjectId]);
        setType("project");
        categories.current = projects[selectedProjectId].categories;
      } else if (orgProjects[selectedProjectId]) {
        setSelectedData(orgProjects[selectedProjectId]);
        setType("project");
        categories.current = orgProjects[selectedProjectId].categories;
      }
      // Check for team projects
      const teamProject = Object.values(teamProjects).find((project) => project[selectedProjectId]);
      if (teamProject) {
        // Find team member id and get project roles
        const projectData = teamProject[selectedProjectId];
        setSelectedData(projectData);
        setType("project");
        categories.current = teamProject[selectedProjectId].categories;
      }
    } else if (selectedMapId) {
      if (userMaps[selectedMapId]) {
        setSelectedData(userMaps[selectedMapId]);
        setType("map");
        categories.current = userMaps[selectedMapId].categories;
      } else if (orgMaps[selectedMapId]) {
        setSelectedData(orgMaps[selectedMapId]);
        setType("map");
        categories.current = orgMaps[selectedMapId].categories;
      }
      // Check for team maps
      const teamMap = Object.values(teamMaps).find((map) => map[selectedMapId]);
      if (teamMap) {
        const mapData = teamMap[selectedMapId];
        setSelectedData(mapData);
        setType("map");
        categories.current = teamMap[selectedMapId].categories;
      }
    } else if (selectedEstateId) {
      if (estates[selectedEstateId]) {
        setSelectedData(estates[selectedEstateId]);
        setType("estate");
        estateMaps.current = getEstateMaps(selectedEstateId, userMaps, orgMaps, teamMaps);
        let cate: string[] = [];
        estateMaps.current.forEach((map) => {
          if (map.categories) {
            for (const el of map.categories) {
              if (!cate.includes(el)) cate.push(el);
            }
          }
        });
        categories.current = cate;
      } else if (orgEstates[selectedEstateId]) {
        setSelectedData(orgEstates[selectedEstateId]);
        setType("estate");
        estateMaps.current = getEstateMaps(selectedEstateId, userMaps, orgMaps, teamMaps);
        let cate: string[] = [];
        estateMaps.current.forEach((map) => {
          if (map.categories) {
            for (const el of map.categories) {
              if (!cate.includes(el)) cate.push(el);
            }
          }
        });
        categories.current = cate;
      }
    } else {
      setSelectedData(null);
    }
  }, [
    selectedMapId,
    selectedProjectId,
    selectedEstateId,
    JSON.stringify(estates),
    JSON.stringify(orgEstates),
    JSON.stringify(projects),
    JSON.stringify(orgProjects),
    JSON.stringify(teamProjects),
    JSON.stringify(userMaps),
    JSON.stringify(orgMaps),
    JSON.stringify(teamMaps),
  ]);

  // --- input checking --- //
  if (!user || !userOrg) return null;

  // --- Save button handler --- //
  async function saveHandler(persons: ContactType[], data: EstateProps | MapProps | ProjectProps) {
    if (selectedPerson?.personType === "audience" && !canManageAudiencePublication(user, userOrg, data, { ...orgEstates, ...estates })) return;
    setLoading(true);
    // Error handling
    if (!type) return;
    // Retrieve audience data
    const audience = persons.filter((el) => el.personType === "audience");
    // Divide data into team members and contacts
    const team: ContactType[] = persons.filter((el) => el.personType && el.personType === "team");
    const existingContacts: ContactType[] = persons.filter(
      (el) => el.id && el.personType && el.personType === "contact",
    );
    const newContacts: ContactType[] = persons.filter(
      (el) => !el.id && el.personType && el.personType === "contact",
    );
    // Call backend
    let promises: Promise<any>[] = [];
    // Update audience state
    if (withAudience && selectedData && canManageAudiencePublication(user, userOrg, data, { ...orgEstates, ...estates }) && (type === "map" || type === "estate")
      && audience.length > 0 && (audience[0].inputValue === "true") !== Boolean(selectedData.audienceActive)) {
      const audienceActive = audience[0].inputValue === "true";
      const prom = callCloudFunction("setAudienceState", {
        estateId: type === "estate" ? data.id : null,
        mapIds: type === "map" ? data.id : null,
        audienceActive,
      }).then((result) => {
        if (type === "map" && result?.data?.status === "SUCCESS") {
          callCloudFunction("trackEvent", {
            event: audienceActive ? mixpanelEvents.mapAudienceActivated : mixpanelEvents.mapAudienceDeactivated,
          });
        }
        return result;
      });
      promises.push(prom);
    }
    // Update team members
    team.forEach(async (el) => {
      const shareEl = [
        {
          inviterId: el.inviterId ? el.inviterId : user?.id ? user?.id : "fail",
          id: data.id,
          role: el.role ? el.role : "viewer",
          categories: el.categories ? el.categories : null,
          expireDate: el.date ? el.date : null,
        },
      ];
      let estates: DataShareItem[] | null = type === "estate" ? shareEl : null;
      let maps: DataShareItem[] | null = type === "map" ? shareEl : null;
      let projects: DataShareItem[] | null = type === "project" ? shareEl : null;
      const prom = callCloudFunction("updateTeamMember", {
        id: el.id,
        orgId: userOrg?.id,
        estates,
        maps,
        projects,
      });
      promises.push(prom);
    });

    // Get langauge based on userOrg
    const lang = getEmailLanguageFromUserOrg(userOrg);
    // Create new contacts
    newContacts.forEach(async (el) => {
      const shareEl = [
        {
          inviterId: user?.id ? user?.id : "fail",
          id: data.id,
          role: el.role ? el.role : "viewer",
          categories: el.categories ? el.categories : null,
          expireDate: el.date ? el.date : null,
        },
      ];
      let estates: DataShareItem[] | null = type === "estate" ? shareEl : null;
      let maps: DataShareItem[] | null = type === "map" ? shareEl : null;
      let projects: DataShareItem[] | null = type === "project" ? shareEl : null;
      const prom = callCloudFunction("createContact", {
        email: el.email,
        language: lang,
        orgId: userOrg?.id,
        estates,
        maps,
        projects,
      });
      promises.push(prom);
    });
    // Update existing contacts
    existingContacts.forEach(async (el) => {
      const shareEl = [
        {
          inviterId: user?.id ? user?.id : "fail",
          id: data.id,
          role: el.role ? el.role : "viewer",
          categories: el.categories ? el.categories : null,
          expireDate: el.date ? el.date : null,
        },
      ];
      let estates: DataShareItem[] | null = type === "estate" ? shareEl : null;
      let maps: DataShareItem[] | null = type === "map" ? shareEl : null;
      let projects: DataShareItem[] | null = type === "project" ? shareEl : null;
      const prom = callCloudFunction("updateContact", {
        id: el.id,
        language: lang,
        userCreated: el.status !== "invite-pending",
        orgId: userOrg?.id,
        estates,
        maps,
        projects,
      });
      promises.push(prom);
    });

    await Promise.all(promises);
    // Log event to mixpanel
    callCloudFunction("trackEvent", { event: mixpanelEvents.mapOrProjectShared });
    setLoading(false);
    shareButtonHandler();
  }

  // --- Set selected data --- //
  const selectedDataChangeHandler = (data: any) => {
    if (!data) {
      setSelectedEstateId(null);
      setSelectedMapId(null);
      setSelectedProjectId(null);
      setType(null);
      return;
    }
    if (data && data.dataElType && data.dataElType === "map" && data.mapId) {
      setSelectedEstateId(null);
      setSelectedMapId(data.mapId);
      setSelectedProjectId(null);
      setType(null);
    } else if (
      data &&
      data.dataElType &&
      ["project", "archivedProject"].includes(data.dataElType) &&
      data.projectId
    ) {
      setSelectedEstateId(null);
      setSelectedMapId(null);
      setSelectedProjectId(data.projectId);
      setType(null);
    } else {
      setSelectedEstateId(data.estateId);
      setSelectedMapId(null);
      setSelectedProjectId(null);
      setType(null);
    }
  };

  // --- Share button --- //
  function shareButtonHandler() {
    setModalOpen(!modalOpen);
    if (resetExtOpenModal) {
      resetExtOpenModal(!openModal);
      setSelectedEstateId(null);
      setSelectedMapId(null);
      setSelectedProjectId(null);
      setType(null);
    }
  }
  // --- Return JSX --- //
  let disabledShareButton = true;
  if (config && config.layout && config.layout.shareData && config.layout.shareData === "enabled") {
    disabledShareButton = false;
  }

  return (
    <div className="share-dialog-wrapper">
      <ShareDialog
        open={modalOpen}
        user={user}
        withDataSelector={withDataSelector !== undefined ? withDataSelector : false}
        withPersonSelector={withPersonSelector !== undefined ? withPersonSelector : false}
        withAudience={Boolean(withAudience && canManageAudiencePublication(user, userOrg, selectedData, { ...orgEstates, ...estates }))}
        loading={loading}
        selectedData={selectedData}
        dataType={type}
        dataOptions={dataOptions}
        allRoleOptions={ROLE_OPTIONS}
        selectedPerson={selectedPerson}
        teamList={teamList}
        contactList={contactList}
        estateMaps={estateMaps.current}
        categoriesFromData={categories.current}
        handleSave={saveHandler}
        handleClose={shareButtonHandler}
        handleChangeSelectedData={selectedDataChangeHandler}
      />

      <div className="share-dialog-button-wrapper">
        {/* Not Mobile */}
        {withButton && !matchesSm && (
          <Button
            className="share-dialog-button"
            variant="contained"
            sx={{
              bgcolor: "primary.light",
              height: "36px",
              "&.Mui-disabled": {
                bgcolor: "secondary.lighter",
                color: "secondary.main",
              },
            }}
            startIcon={<PeopleIcon />}
            disabled={disabledShareButton || !selectedMapId || selectedProjectId !== null}
            onClick={shareButtonHandler}
          >
            {translate("map-toolbar-share-button-text")}
          </Button>
        )}

        {/* Mobile */}
        {withButton && matchesSm && (
          <IconButton
            variant="contained"
            sx={{ bgcolor: "primary.light", height: "36px" }}
            disabled={disabledShareButton || !selectedMapId}
            onClick={shareButtonHandler}
          >
            <PeopleIcon fontSize="small" />
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default ShareData;
